import { defineStore } from 'pinia'
import { useApiRequest } from '@/api'
import { type BrandKit, useBrandFontsResource } from '../resources'
import { useBrandKitStore } from './brand-kit.store'

type FontsResource = ReturnType<typeof useBrandFontsResource>
type KitFontsBody = Parameters<FontsResource['setBrandFonts']>[2]
type AddFontBody = Parameters<FontsResource['setBrandFont']>[2]
type ImportFontBody = Parameters<FontsResource['importBrandFont']>[2]

export const useBrandFontsStore = defineStore('brand-fonts', () => {
  const { setBrandFonts, setBrandFont, importBrandFont, delBrandFont } = useBrandFontsResource()
  const kitStore = useBrandKitStore()
  const { run, loading, failure } = useApiRequest()

  const saveKitFonts = async (organizationId: string, brandId: string, body: KitFontsBody) => {
    const written = await run<{ kit: BrandKit['kit']; fonts: BrandKit['fonts'] }>(() => setBrandFonts(organizationId, brandId, body))
    const kit = kitStore.kits[brandId]
    if (written && kit) kitStore.kits = { ...kitStore.kits, [brandId]: { ...kit, kit: written.kit, fonts: written.fonts } }
    return written
  }

  const addKitFont = async (organizationId: string, brandId: string, body: AddFontBody) => {
    const font = await run<BrandKit['fonts'][number]>(() => setBrandFont(organizationId, brandId, body))
    // A library face can arrive with an italic sibling, and only the kit read carries a download url.
    if (font) await kitStore.loadKit(organizationId, brandId)
    return font
  }

  const importKitFont = async (organizationId: string, brandId: string, body: ImportFontBody) => {
    const font = await run<BrandKit['fonts'][number]>(() => importBrandFont(organizationId, brandId, body))
    if (font) await kitStore.loadKit(organizationId, brandId)
    return font
  }

  const removeKitFont = async (organizationId: string, brandId: string, fontId: string) => {
    await run(() => delBrandFont(organizationId, brandId, fontId))
    if (failure.value) return false
    const kit = kitStore.kits[brandId]
    if (kit) kitStore.kits = { ...kitStore.kits, [brandId]: { ...kit, fonts: kit.fonts.filter(font => font.id !== fontId) } }
    return true
  }

  return { loading, failure, saveKitFonts, addKitFont, importKitFont, removeKitFont }
})
